import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Loader2 } from "lucide-react";
import LegalConsentGate from "@/components/legal/LegalConsentGate";

/**
 * Route guard for admin-only pages.
 *
 * Layered defense:
 *  1. Must be authenticated (redirect → /auth otherwise)
 *  2. Must have `admin` row in public.user_roles (checked via SECURITY DEFINER
 *     `has_role()` RPC so it cannot be spoofed client-side)
 *  3. RLS on every admin-affected table is the final wall
 *
 * Children only mount AFTER the role check resolves true — the admin UI never
 * renders to a non-admin, even briefly.
 */
export default function RequireAdmin({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { data: isAdmin, isLoading: roleLoading } = useAdminRole();

  useEffect(() => {
    if (!authLoading && !roleLoading && user && isAdmin === false) {
      // Silent redirect: members never see admin wording.
    }
  }, [authLoading, roleLoading, user, isAdmin]);

  if (authLoading || (user && roleLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <LegalConsentGate />
      {children}
    </>
  );
}
